import React, { FC, useEffect, useState } from 'react';

import { InitConfig } from '@solidgate/client-sdk-loader';
import Payment from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import Loader from '~/components/atoms/Loader';
import PaymentError from '~/components/molecules/PaymentError';
import Guarantee from '~/components/organisms/SolidCheckout/components/PaymentForm/V2/Guarantee';
import { usePaymentFormLogic } from '~/components/organisms/SolidCheckout/hooks/usePaymentFormLogic';
import { PAYMENT_METHODS, SOLID_PAYMENT_METHOD_MAPPER } from '~/types/payment';

import { defaultFormStyles, getDefaultFormParams } from './formConfig';
import PaymentButton from './PaymentButton';
import styles from './styles.module.scss';
import { PaymentFormProps } from '../../../types';

type Props = PaymentFormProps & {
  onPaymentView?: () => void;
  formParams?: InitConfig['formParams'];
  formStyles?: InitConfig['styles'];
  SummaryComponent?: FC;
};

const PaymentFormV2: FC<Props> = ({
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onPaymentMethodChange,
  onPaymentView,
  onCreateCardIntentError,
  onFormReady,
  paymentApi,
  formStyles,
  formParams,
  SummaryComponent,
}) => {
  const { t } = useTranslation('general');
  const [activeCard, setActiveCard] = useState<PAYMENT_METHODS>(
    PAYMENT_METHODS.ONE_PAY,
  );

  const {
    isLoading,
    paymentError,
    merchantData,
    paypalBtnRef,
    appleContainerRef,
    googleContainerRef,
    handleCloseError,
    handlePaymentSuccess,
    handlePaymentFail,
    handlePaymentSubmit,
    isPaypalAvailable,
  } = usePaymentFormLogic({
    paymentApi,
    onPaymentSuccess,
    onPaymentFail,
    onPaymentSubmit,
    onCreateCardIntentError,
    onFormReady,
  });

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    if (onPaymentMethodChange) {
      onPaymentMethodChange(type);
    }

    setActiveCard(type);
  };

  useEffect(() => {
    if (onPaymentView) {
      onPaymentView();
    }
  }, []);

  return (
    <div className={styles.container}>
      {isLoading && <Loader />}

      {paymentError && (
        <PaymentError
          paymentError={paymentError}
          handleCloseError={handleCloseError}
        />
      )}

      {merchantData && !paymentError && (
        <Swiper
          slidesPerView={1}
          spaceBetween={16}
          initialSlide={0}
          centeredSlides
          onSwiper={(swiper): void => {
            activeCard === PAYMENT_METHODS.ONE_PAY && swiper.slideNext(); // By invoking this method we trigger slider for IOS Chrome
          }}
          className={clsx(styles.slider, {
            [styles.is_disabled]: isLoading,
          })}
        >
          <div className={styles.header}>
            <div className={styles.header__buttons}>
              {[PAYMENT_METHODS.ONE_PAY, PAYMENT_METHODS.CARD].map((type) => (
                <PaymentButton
                  key={type}
                  isActive={activeCard === type}
                  onClick={handlePaymentMethodChange}
                  type={type}
                />
              ))}
            </div>

            {SummaryComponent && (
              <div className={styles.header__summary}>
                <SummaryComponent />
              </div>
            )}
          </div>

          <SwiperSlide className={styles.slide}>
            <Payment
              googlePayContainerRef={googleContainerRef}
              applePayContainerRef={appleContainerRef}
              merchantData={merchantData}
              formParams={formParams || getDefaultFormParams(t)}
              styles={formStyles || defaultFormStyles}
              onFail={(e): void =>
                handlePaymentFail(
                  e.message || '',
                  SOLID_PAYMENT_METHOD_MAPPER[e.entity],
                )
              }
              onSuccess={async (e): Promise<void> =>
                await handlePaymentSuccess(e.order)
              }
              onMounted={(): void => {
                window.setTimeout(() => {
                  onFormReady && onFormReady();
                }, 500);
              }}
              onSubmit={(e): void =>
                handlePaymentSubmit(SOLID_PAYMENT_METHOD_MAPPER[e.entity])
              }
            />
          </SwiperSlide>

          <SwiperSlide className={styles.slide}>
            <div
              className={styles['slide__pay_button']}
              ref={appleContainerRef}
            />
            <div
              className={styles['slide__pay_button']}
              ref={googleContainerRef}
            />
            {isPaypalAvailable && (
              <div
                className={styles['slide__pay_button']}
                id="paypal-button"
                ref={paypalBtnRef}
              />
            )}
          </SwiperSlide>
        </Swiper>
      )}

      <div className={styles.guarantee}>
        <Guarantee />
      </div>
    </div>
  );
};

export default PaymentFormV2;
